import _ from 'lodash';

import actionTypes from '~actionTypes';
import { createNamespacer, createReducer } from '~utils/reducers';

const BoGSAccountNamespacer = createNamespacer('BUSINESS_OWNER_GS_ACCOUNT_SETTINGS');
const accountSettingsAT = actionTypes.businessOwner.globalSettings.accountSettings;

const initialState = {
	accountDetails: {
		fullName: '',
		companyName: '',
		address: '',
		state: '',
		city: '',
		zipCode: '',
		phone: '',
		email: '',
		website: '',
		needsRegions: false,
		regions: [],
	},
	updateInProgress: false,
	fullPageError: false,
	showRegionModal: false,
	modalRegion: {},
	regionSaveInProgress: false,
	regionSaveError: '',
	errorMessage: '',
	errorFields: {
		fullName: false,
		companyName: false,
		address: false,
		state: false,
		city: false,
		zipCode: false,
		phone: false,
		email: false,
		website: false,
	},
	settings: {
		businessId: null,
		id: null,
		isWeightAfterProcessing: false,
		isWeightBeforeProcessing: false,
		isWeightDuringIntake: false,
		isWeightUpOnCompletion: false,
		isCustomPreferencesEnabled: false,
		requiresEmployeeCode: false,
		salesWeight: 'DURING_INTAKE',
		isCustomUrl: false,
		termsOfServiceUrl: '',
		receiptFooterMessage: 'Thank you for your order.',
		isHangDryEnabled: false,
		hangDryInstructions: '',
		hasSelfServe: false,
		hasFullServe: false,
		isWalkInOrderMinimumEnabled: false,
		isDeliveryOrderMinimumEnabled: false,
	},

	settingSaveInProgress: false,
	settingsSaveError: '',
	taxes: [],
	taxSaveInProgress: false,
	taxSaveError: '',
	showTaxModal: false,
};

const handlers = {
	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_DETAILS)]: (state, action) => {
		return {
			...state,
			accountDetails: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.UPDATE_ACCOUNT_DETAILS)]: (
		state,
		action,
	) => {
		let accountDetails = { ...state.accountDetails };
		accountDetails[action.payload.field] = action.payload.value;
		return {
			...state,
			accountDetails,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.RESET_ACCOUNT_DETAILS)]: (state) => {
		return {
			...state,
			accountDetails: initialState.accountDetails,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_DETAILS_ERROR)]: (
		state,
		action,
	) => {
		let errorMessage =
			_.get(action, 'payload.errorMessage') || 'Something went wrong';
		return {
			...state,
			errorMessage,
			fullPageError: action.payload.fullPageError,
			errorFields: action.payload.errorFields || {},
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_DETAILS_FIELD_ERROR)]: (
		state,
		action,
	) => {
		return {
			...state,
			errorFields: {
				...state.errorFields,
				[action.payload.field]: action.payload.error,
			},
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.RESET_ACCOUNT_DETAILS_ERROR)]: (state) => {
		return {
			...state,
			errorMessage: initialState.errorMessage,
			fullPageError: initialState.fullPageError,
			errorFields: initialState.errorFields,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.UPDATE_CALL_IN_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			updateInProgress: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_SHOW_REGION_MODAL)]: (state, action) => {
		return {
			...state,
			showRegionModal: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_MODAL_REGION)]: (state, action) => {
		let modalRegion;
		if (!action.payload) {
			modalRegion = {
				name: '',
				districts: [],
			};
		} else {
			let region = state.accountDetails.regions.find(
				(region) => region.id === action.payload,
			);
			if (!region) {
				modalRegion = {};
			} else {
				modalRegion = region;
			}
		}

		return {
			...state,
			modalRegion,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.ADD_NEW_DISTRICT_TO_MODAL_REGION)]: (
		state,
	) => {
		const modalRegion = { ...state.modalRegion };
		if (
			modalRegion.districts &&
			(modalRegion.districts.length === 0 ||
				(modalRegion.districts[modalRegion.districts.length - 1].name &&
					modalRegion.districts[modalRegion.districts.length - 1].name.trim()))
		) {
			const districts = modalRegion.districts.slice();
			districts.push({
				name: '',
			});
			modalRegion.districts = districts;
		} else {
			return state;
		}
		return {
			...state,
			modalRegion,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_REGION_MODAL_TEXT_FIELD_VALUE)]: (
		state,
		action,
	) => {
		const modalRegion = { ...state.modalRegion };

		if (action.payload.field === 'region') {
			modalRegion.name = action.payload.value;
		} else if (action.payload.field === 'district') {
			let districts = modalRegion.districts.slice();
			let updatedDistrict = { ...districts[action.payload.index] };
			updatedDistrict.name = action.payload.value;
			districts[action.payload.index] = updatedDistrict;
			modalRegion.districts = districts;
		}

		return {
			...state,
			modalRegion,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_REGION_SAVE_IN_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			regionSaveInProgress: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_REGION_SAVE_ERROR)]: (state, action) => {
		return {
			...state,
			regionSaveError: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_SETTINGS)]: (state, action) => {
		return {
			...state,
			settings: action.payload,
			settingsCopy: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.UPDATE_ACCOUNT_SETTINGS)]: (
		state,
		action,
	) => {
		return {
			...state,
			settings: {
				...state.settings,
				[action.payload.field]: action.payload.value,
			},
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_SETTINGS_ERROR)]: (
		state,
		action,
	) => {
		if (action.payload && state.settingsCopy) {
			return {
				...state,
				settingsSaveError: action.payload,
				settings: { ...state.settingsCopy },
			};
		}
		return {
			...state,
			settingsSaveError: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_SETTING_SAVE_IN_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			settingSaveInProgress: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_TAXES)]: (state, action) => {
		const taxes = _.sortBy(action.payload, [(tax) => tax.name.toLowerCase()]);
		return {
			...state,
			taxes,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_ACCOUNT_TAXES_ERROR)]: (
		state,
		action,
	) => {
		return {
			...state,
			taxSaveError: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_TAX_SAVE_IN_PROGRESS)]: (
		state,
		action,
	) => {
		return {
			...state,
			taxSaveInProgress: action.payload,
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_SHOW_TAX_MODAL)]: (state, action) => {
		return {
			...state,
			showTaxModal: action.payload,
		};
	},
	[BoGSAccountNamespacer(accountSettingsAT.SET_TERMS_OF_SERVICE_URL)]: (
		state,
		action,
	) => {
		return {
			...state,
			settings: {
				...state.settings,
				termsOfServiceUrl: action.payload,
			},
		};
	},

	[BoGSAccountNamespacer(accountSettingsAT.SET_RECEIPT_CUSTOM_MESSAGE)]: (
		state,
		action,
	) => {
		return {
			...state,
			settings: {
				...state.settings,
				receiptFooterMessage: action.payload,
			},
		};
	},
};

export default createReducer(initialState, handlers, [
	'BUSINESS_OWNER_GS_ACCOUNT_SETTINGS',
]);
